import { gql, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import {
  Badge,
  FormControl,
  FormLabel,
  IconButton,
  Input,
} from '@apollo/orbit';
import classnames from 'classnames';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { LinkEndpointModalData } from 'src/app/graph/explorerPage/connectionSettingsModal/ConnectionSettingsModal';
import {
  Reachability,
  useEndpointReachability,
} from 'src/app/graph/hooks/useEndpointReachability';
import { useIsLoggedIn } from 'src/app/graph/hooks/useIsLoggedIn';
import { useSchema } from 'src/app/graph/hooks/useSchema';
import {
  explorerRouteConfig,
  sandboxCheckRouteConfig,
  sandboxDiffRouteConfig,
  schemaRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { GraphBehavioralFlags } from 'src/app/graph/variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePerKeyLocalStorage } from 'src/hooks/usePerKeyLocalStorage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { CollectionsFeedbackCallout } from '../globalHeader/CollectionsFeedbackCallout';

import {
  PublishThisGraphButton,
  PublishThisGraphButtonFragment,
} from './PublishThisGraphButton';
import { RefetchSchemaButton } from './RefetchSchemaButton';

const ConnectivityBadge = ({
  shouldAutoUpdateSchema,
  reachability,
}: {
  shouldAutoUpdateSchema: boolean;
  reachability: Reachability;
}) => {
  return !shouldAutoUpdateSchema ? (
    // Pause icon
    <svg className="block size-2" viewBox="0 0 7 7">
      <g stroke="none" strokeWidth="1" fill="currentColor" fillRule="evenodd">
        <rect x="0" y="0" width="3" height="7" rx="1" />
        <rect x="4" y="0" width="3" height="7" rx="1" />
      </g>
    </svg>
  ) : reachability === 'reachable' ? (
    <div className="size-2 rounded bg-icon-success" />
  ) : (
    <div className="size-2 rounded bg-icon-error" />
  );
};

export const GlobalSandboxHeader = ({
  graphBehavioralFlags,
}: {
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  const [sandboxUrl, setSandboxUrl] = useLocalStorage('sandboxUrl');
  const { isLoggedIn } = useIsLoggedIn();
  const [currentSandboxUrl, setCurrentSandboxUrl] = useState(sandboxUrl);
  const trackCustomEvent = useTrackCustomEvent();

  const { data } = useQuery<
    GraphQLTypes.GlobalSandboxHeaderQuery,
    GraphQLTypes.GlobalSandboxHeaderQueryVariables
  >(gql`
    query GlobalSandboxHeaderQuery {
      me {
        id
        ... on User {
          id
          ...PublishThisGraphButtonFragment
        }
      }
    }
    ${PublishThisGraphButtonFragment}
  `);

  const setSandboxUrlDebounced = useMemo(
    () => debounce(setSandboxUrl, 300),
    [setSandboxUrl],
  );

  // We need to keep this value in sync if the sandbox url changes elsewhere
  // (for example: in the connection settings modal)
  useEffect(() => {
    setCurrentSandboxUrl(sandboxUrl);
  }, [sandboxUrl]);

  const [sandboxConnectionSettings, setSandboxConnectionSettings] =
    usePerKeyLocalStorage({
      localStorageKey: 'sandboxConnectionSettings',
      key: sandboxUrl,
      initialValue:
        graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
    });
  const [hasDismissedSubgraphsInSandboxFeatureCallOut] = useLocalStorage(
    'hasDismissedSubgraphsInSandboxFeatureCallOut',
  );
  const [hasDismissedOperationCollectionsFeatureCallOut] = useLocalStorage(
    'hasDismissedOperationCollectionsFeatureCallOut',
  );

  const endpointReachability = useEndpointReachability();
  const location = useLocation<{
    linkEndpointModalData: LinkEndpointModalData;
  }>();

  const isSandboxCheckPage =
    useRouteMatch(sandboxCheckRouteConfig.definition) !== null;
  const isSandboxDiffPage =
    useRouteMatch(sandboxDiffRouteConfig.definition) !== null;

  const { subgraphSdl } = useSchema();

  const user = useMemo(
    () => (data?.me?.__typename === 'User' ? data.me : undefined),
    [data],
  );

  const isExplorerRoute =
    useRouteMatch(explorerRouteConfig.definition) !== null;
  const isReadmeRoute =
    useRouteMatch(variantHomepageRouteConfig.definition) !== null;
  const isSchemaRoute = useRouteMatch(schemaRouteConfig.definition) !== null;

  return (
    <>
      <div className="flex h-7 min-w-72 items-center overflow-hidden rounded border border-primary text-primary">
        <Tooltip
          label={
            <>
              <div className="mb-2 text-base font-semibold">Studio Sandbox</div>
              <p>
                The Sandbox is loaded with the schema from your endpoint's
                introspection, rather than a schema from the Studio registry. As
                such, some of the features here are limited.
              </p>
            </>
          }
        >
          <span className="flex h-full cursor-default items-center border-r border-primary bg-neutral px-2 text-xs font-semibold uppercase tracking-wider text-neutral">
            Sandbox
          </span>
        </Tooltip>
        <FormControl
          className={classnames(
            graphBehavioralFlags.sandboxEndpointIsEditable
              ? 'cursor-pointer'
              : 'cursor-default',
          )}
        >
          <FormLabel className="sr-only">Sandbox header input</FormLabel>
          <Input
            variant="hidden"
            leftElement={
              <Tooltip
                label={
                  sandboxConnectionSettings.shouldAutoUpdateSchema
                    ? 'Turn off schema polling'
                    : 'Turn on schema polling'
                }
              >
                <IconButton
                  data-testid="connectivity-badge"
                  aria-label={
                    sandboxConnectionSettings.shouldAutoUpdateSchema
                      ? 'Turn off schema polling'
                      : 'Turn on schema polling'
                  }
                  className="h-6 min-w-6"
                  icon={
                    <ConnectivityBadge
                      reachability={endpointReachability}
                      shouldAutoUpdateSchema={
                        sandboxConnectionSettings.shouldAutoUpdateSchema
                      }
                    />
                  }
                  onClick={() => {
                    const action =
                      sandboxConnectionSettings.shouldAutoUpdateSchema
                        ? 'off'
                        : 'on';
                    trackCustomEvent({
                      action: `turn_${action}_polling`,
                      category: 'Sandbox',
                    });
                    setSandboxConnectionSettings((prev) => {
                      return {
                        ...prev,
                        shouldAutoUpdateSchema: !prev.shouldAutoUpdateSchema,
                      };
                    });
                  }}
                  variant="hidden"
                  size="sm"
                />
              </Tooltip>
            }
            readOnly={!graphBehavioralFlags.sandboxEndpointIsEditable}
            type="text"
            aria-label="Sandbox header input"
            value={currentSandboxUrl}
            className="min-w-40"
            placeholder="http://localhost:4000"
            onBlur={(event) => {
              const newValue = !event.target.value
                ? ''
                : (/^(https|http|ws|wss)/i.test(event.target.value)
                    ? event.target.value
                    : `${
                        isLocalUrl(event.target.value) ? 'http:' : 'https:'
                      }//${event.target.value}`
                  ).replace(/\s/g, '');
              // immediately set the local storage on blur,
              // so when folks open the connection settings modal
              // it always reflects their current endpoint
              setSandboxUrlDebounced.cancel();
              setSandboxUrl(newValue);
            }}
            onChange={(e) => {
              setCurrentSandboxUrl(e.target.value);
              setSandboxUrlDebounced(e.target.value);
            }}
            rightElement={
              <Tooltip label="Open connection settings">
                <IconButton
                  aria-label="Connection settings"
                  variant="hidden"
                  size="sm"
                  icon={<IconSettings />}
                  data-testid="sandbox-header-connection-settings-button"
                  as={Link}
                  to={{
                    ...setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.connectionSettings,
                    }),
                    state: {
                      ...location.state,
                      linkEndpointModalData: {
                        openingIntent: 'Set url',
                      },
                    },
                  }}
                />
              </Tooltip>
            }
          />
        </FormControl>
      </div>
      {subgraphSdl && endpointReachability === 'reachable' && (
        <Badge className="ml-4" variant="neutralOutlined">
          Subgraph
        </Badge>
      )}
      {!sandboxConnectionSettings.shouldAutoUpdateSchema && (
        <RefetchSchemaButton />
      )}
      {!subgraphSdl && <PublishThisGraphButton user={user} />}
      {(isSandboxCheckPage || isSandboxDiffPage) &&
      hasDismissedSubgraphsInSandboxFeatureCallOut &&
      isLoggedIn ? (
        <>
          <div className="flex-1" />
          <div className="flex items-center truncate text-sm text-inverted">
            <IconInfo className="mr-2 text-placeholder" />
            <span className="w-full truncate">
              We recently added the ability to diff schemas and run checks from
              Sandbox. We would{' '}
              <ClickableText
                as="a"
                target="_blank"
                className="text-link no-underline hover:no-underline"
                rel="noopener noreferrer"
                href="https://forms.gle/4G2cEumrh85jgDF1A"
              >
                love your feedback. 👂
              </ClickableText>
            </span>
          </div>
        </>
      ) : hasDismissedOperationCollectionsFeatureCallOut &&
        isLoggedIn &&
        (isExplorerRoute || isReadmeRoute || isSchemaRoute) ? (
        <>
          <div className="flex-1" />
          <CollectionsFeedbackCallout />
        </>
      ) : null}
    </>
  );
};
